import { useField } from 'formik'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useIntl } from 'react-intl'
import DateTimeField from '../DateTimeField'
import { datetimeLocalToGraphDateTimeOffset, toDateTimeLocalInput } from '../../util/date.util'

const propTypes = {
  /**
   * id of the field. The id passed is also bound to the field's name
   */
  id: PropTypes.string.isRequired,
  /**
   * If exists, calls it with the value for custom additional actions
   * Formik setFieldValue will always run before it
   */
  onChange: PropTypes.func,
  /**
   * When true, Formik stores an ISO-8601 UTC string (Graph DateTimeOffset); the control still uses `datetime-local`.
   */
  useGraphDateTimeOffset: PropTypes.bool,
  /** Optional hint below the control (merged with Graph UTC hint when `useGraphDateTimeOffset`). */
  info: PropTypes.string,
}

/**
 * Connects the DateTimeField with Formik.
 */
function FormikDateTimeField(props) {
  const { id, onChange = null, useGraphDateTimeOffset = false, info: infoProp = undefined, ...rest } = props ?? {}
  const intl = useIntl()
  const [field, meta, helpers] = useField(id)

  const displayValue = useGraphDateTimeOffset ? toDateTimeLocalInput(field.value) : (field.value ?? '')

  const graphHint = useGraphDateTimeOffset ? intl.formatMessage({ id: 'GENERAL.GRAPH_DATETIME_OFFSET_HINT' }) : ''
  const info = [infoProp, graphHint].filter(Boolean).join(' ').trim()

  const handleChange = useCallback(
    (event) => {
      if (useGraphDateTimeOffset) {
        const iso = datetimeLocalToGraphDateTimeOffset(event.target.value)
        helpers.setValue(iso)
      } else {
        field.onChange(event)
      }
      if (onChange) {
        onChange(event.target.value)
      }
    },
    [useGraphDateTimeOffset, field, helpers, onChange],
  )

  const handleBlur = useCallback(
    (event) => {
      if (useGraphDateTimeOffset) {
        helpers.setTouched(true)
      } else {
        field.onBlur(event)
      }
    },
    [useGraphDateTimeOffset, field, helpers],
  )

  return (
    <DateTimeField
      {...rest}
      id={id}
      name={id}
      onChange={handleChange}
      onBlur={handleBlur}
      value={displayValue}
      errors={meta.touched && meta.error ? [meta.error] : []}
      info={info}
    />
  )
}

FormikDateTimeField.propTypes = propTypes

export default FormikDateTimeField
